import * as React from 'react';

import { useKeytipRef } from '@fluentui/react';
import { useMergedRefs } from '@fluentui/react-hooks';
import {
  CheckboxBase,
  ICheckbox,
  ICheckboxProps,
  ICheckboxStyleProps,
  ICheckboxStyles,
} from '@fluentui/react/lib/Checkbox';
import { ITheme } from '@fluentui/react/lib/Styling';
import { styled } from '@fluentui/react/lib/Utilities';

import { dataUniqueId, generateAriaDescription } from '@1js/acui-common';
import { AppContext } from '@1js/acui-context';
import { checkExperiment, ribbonExperiments } from '@1js/acui-experiments';
import { iconNameToIconProps } from '@1js/acui-icons';
import { defaultKeytipExecute } from '@1js/acui-keytip';
import { AppThemeConsumer } from '@1js/acui-theme';
import { AppTooltipHost, getTooltipText } from '@1js/acui-tooltip';

import { AppCheckboxInternalProps } from './AppCheckbox.Props';
import { getCheckboxStyles, getStyles } from './AppCheckbox.styles';
import { deriveCheckboxKeytip } from './deriveCheckboxKeytip';

export interface AppCheckboxState {
  focused: boolean;
}

const Checkbox = styled<ICheckboxProps, ICheckboxStyleProps, ICheckboxStyles>(CheckboxBase, getStyles);

export class AppCheckbox extends React.PureComponent<AppCheckboxInternalProps, AppCheckboxState> {
  private checkboxRef = React.createRef<HTMLDivElement>();
  private checkBox = React.createRef<ICheckbox>();
  private tooltipHost: AppTooltipHost | undefined;

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  constructor(props: AppCheckboxInternalProps) {
    super(props);
    this.state = {
      // NOTE: To style the wrapper div of the checkbox (root) when the input is focused we need a way to keep track of it in the component.
      // :focus-within selector is not supported by IE11 or Edge so this workaround was added to make visual changes to a parent container, once one of its children has focus.
      // Once :focus-within is supported in Edge and we no longer support IE11, we can replace this with selector based styling.
      focused: false,
    };
  }

  public render() {
    const { label, checked, disabled, dataAutomationType } = this.props;
    const { ariaDescriptionId, ariaDescriptionElement } = generateAriaDescription(
      this.props.id,
      this.props.customTooltip
    );

    return (
      <AppThemeConsumer>
        {(theme: ITheme) => (
          <>
            <Checkbox
              ref={useMergedRefs(
                useKeytipRef<HTMLDivElement>({
                  keytipProps: deriveCheckboxKeytip(this.props.keytipProps, this.keytipExecute),
                }),
                this.checkboxRef
              )}
              id={this.props.id}
              inputProps={
                {
                  onFocus: this.onFocus,
                  onBlur: this.onBlur,
                  'data-nav': this.props.dataNav,
                  'data-unique-id': dataUniqueId(this.props),
                  'data-automation-type': dataAutomationType || 'AppCheckbox',
                  role: this.props.isMenuItem ? 'menuitemcheckbox' : 'checkbox',
                  'aria-haspopup': this.props.isMenuExpanded !== undefined ? 'menu' : undefined,
                  'aria-expanded': this.props.isMenuExpanded,
                } as React.ButtonHTMLAttributes<HTMLButtonElement>
              }
              componentRef={this.checkBox}
              checked={checked}
              disabled={disabled}
              label={label}
              onChange={this.onExecute}
              checkmarkIconProps={
                this.props.iconColor
                  ? iconNameToIconProps(this.props.checkmarkIcon, this.props.iconColor)
                  : iconNameToIconProps(this.props.checkmarkIcon)
              }
              ariaPositionInSet={this.props.positionInSet}
              ariaSetSize={this.props.setSize}
              title={this.props.title}
              styles={getCheckboxStyles(theme, this.state.focused, !!checked, !!disabled, this.props.styles)}
              ariaLabel={label}
              ariaDescribedBy={ariaDescriptionElement && ariaDescriptionId}
            />
            {ariaDescriptionElement}
          </>
        )}
      </AppThemeConsumer>
    );
  }

  private onFocus = () => {
    this.setState({ focused: true });
  };

  private onBlur = () => {
    this.setState({ focused: false });
  };

  public componentDidMount() {
    if (this.context.setTooltipElement && this.context.updateTooltipsHidden) {
      this.tooltipHost = new AppTooltipHost({
        updateTooltipsHidden: this.context.updateTooltipsHidden,
        setTooltipElement: this.context.setTooltipElement,
        refElement: this.checkboxRef,
        controlId: this.props.id,
        controlLabel: getTooltipText(false /* hideLabel */, this.props.label, this.props.customTooltip),
        tooltipDirection: this.props.tooltipDirection,
        performSetOnNextFrame: !!checkExperiment(this.context, ribbonExperiments.delayRenderTooltipsIsEnabled),
      });
      this.tooltipHost.registerTooltipHostActions();
    }
  }

  public componentWillUnmount() {
    if (this.tooltipHost) {
      this.tooltipHost.unregisterTooltipHostActions();
    }
  }

  public componentDidUpdate(prevProps: AppCheckboxInternalProps) {
    if (
      this.tooltipHost &&
      (prevProps.customTooltip !== this.props.customTooltip || prevProps.label !== this.props.label)
    ) {
      this.tooltipHost.updateTooltip(getTooltipText(false /* hideLabel */, this.props.label, this.props.customTooltip));
    }
  }

  private keytipExecute = (executeTarget: HTMLElement) => {
    defaultKeytipExecute(executeTarget, this.onExecute);
  };

  private onExecute = () => {
    if (this.props.onExecute) {
      this.props.onExecute({ id: this.props.id, checked: !this.props.checked, location: this.props.location });
    }
  };
}
